import { defineStore } from 'pinia';
import { getDiditToken, getUserKyc, pcCardCreatePre, v1CommonCityList, v1CommonCountryList, v1CommonProvinceList } from '@/api/card';
import { resolveDiditVerificationUrl } from '@/view/card/utils';
import { countryCodeItems, findCountryCodeItemByIso2 } from '@/utils/country-code';

function defaultForm() {
  return {
    phoneCode: '',
    phoneNumber: '',
    email: '',
    code: '',
    addressLine1: '',
    postalCode: '',
    countryId: '',
    provinceId: '',
    cityId: ''
  };
}

function defaultState() {
  return {
    loading: false,
    submitting: false,
    checking: false,
    holderStatus: 0,
    rejectReason: '',
    verificationUrl: '',
    verificationToken: '',
    diditSdkState: 'idle',
    diditSessionId: '',
    diditSessionStatus: '',
    diditLastEventType: '',
    diditLastEventData: null,
    diditErrorMessage: '',
    kycData: {},
    countries: [],
    provinces: [],
    cities: [],
    form: defaultForm()
  };
}

function normalized(value) {
  return value === undefined || value === null ? '' : String(value).trim();
}

function matchArea(list, { idKey, idValue, codeKey, codeValue, nameValue } = {}) {
  const matchedId = normalized(idValue);
  if (matchedId && idKey) {
    const byId = list.find((item) => normalized(item?.[idKey]) === matchedId);
    if (byId) {
      return byId;
    }
  }

  const matchedCode = normalized(codeValue).toUpperCase();
  if (matchedCode && codeKey) {
    const byCode = list.find((item) => normalized(item?.[codeKey]).toUpperCase() === matchedCode);
    if (byCode) {
      return byCode;
    }
  }

  const matchedName = normalized(nameValue);
  if (!matchedName) {
    return null;
  }

  return list.find((item) => {
    const names = [item?.nameCn, item?.nameEn, item?.name].filter(Boolean).map((name) => normalized(name));
    return names.includes(matchedName);
  }) || null;
}

export const useCardKycStore = defineStore('card-kyc', {
  state: () => defaultState(),
  getters: {
    selectedCountry(state) {
      return state.countries.find((item) => normalized(item?.countryId) === normalized(state.form.countryId)) || null;
    },
    selectedProvince(state) {
      return state.provinces.find((item) => normalized(item?.provinceId) === normalized(state.form.provinceId)) || null;
    },
    selectedCity(state) {
      return state.cities.find((item) => normalized(item?.cityId) === normalized(state.form.cityId)) || null;
    },
    countryName() {
      return this.selectedCountry?.nameCn || this.selectedCountry?.nameEn || this.selectedCountry?.name || '';
    },
    provinceName() {
      return this.selectedProvince?.nameCn || this.selectedProvince?.nameEn || this.selectedProvince?.name || '';
    },
    cityName() {
      return this.selectedCity?.nameCn || this.selectedCity?.nameEn || this.selectedCity?.name || '';
    }
  },
  actions: {
    reset() {
      Object.assign(this, defaultState());
    },
    resetForm() {
      this.form = defaultForm();
      this.provinces = [];
      this.cities = [];
      this.verificationUrl = '';
      this.verificationToken = '';
      this.resetDiditRuntime();
    },
    resetDiditRuntime() {
      this.diditSdkState = 'idle';
      this.diditSessionId = '';
      this.diditSessionStatus = '';
      this.diditLastEventType = '';
      this.diditLastEventData = null;
      this.diditErrorMessage = '';
    },
    setVerificationPayload({ url = '', token = '' } = {}) {
      const resolvedToken = normalized(token);
      this.verificationUrl = resolveDiditVerificationUrl({ url, token: resolvedToken });
      this.verificationToken = resolvedToken;
    },
    setDiditState(state = 'idle', errorMessage = '') {
      this.diditSdkState = normalized(state) || 'idle';
      this.diditErrorMessage = normalized(errorMessage);
    },
    setDiditSession({ sessionId = '', status = '' } = {}) {
      if (normalized(sessionId)) {
        this.diditSessionId = normalized(sessionId);
      }
      if (normalized(status)) {
        this.diditSessionStatus = normalized(status);
      }
    },
    setDiditEvent(event = {}) {
      this.diditLastEventType = normalized(event?.type);
      this.diditLastEventData = event?.data || null;
      this.setDiditSession({
        sessionId: event?.data?.sessionId,
        status: event?.data?.status
      });
    },
    setDiditCompletion(result = {}) {
      this.setDiditSession({
        sessionId: result?.session?.sessionId,
        status: result?.session?.status
      });
    },
    setDiditError(errorMessage = '') {
      this.diditErrorMessage = normalized(errorMessage);
    },
    async ensureCountries() {
      if (this.countries.length) {
        return this.countries;
      }
      const result = await v1CommonCountryList({ address: '' });
      this.countries = Array.isArray(result?.list) ? result.list : [];
      return this.countries;
    },
    async loadProvinces() {
      if (!this.form.countryId) {
        this.provinces = [];
        return [];
      }
      const result = await v1CommonProvinceList({ countryId: this.form.countryId });
      this.provinces = Array.isArray(result?.list) ? result.list : [];
      return this.provinces;
    },
    async loadCities() {
      if (!this.form.countryId || !this.form.provinceId) {
        this.cities = [];
        return [];
      }
      const result = await v1CommonCityList({
        countryId: this.form.countryId,
        provinceId: this.form.provinceId
      });
      this.cities = Array.isArray(result?.list) ? result.list : [];
      return this.cities;
    },
    async selectPhoneCode(index) {
      const item = countryCodeItems[index];
      this.form.phoneCode = item?.dial_code || '';
    },
    getCountryCodeIndex(dialCode) {
      return countryCodeItems.findIndex((item) => normalized(item?.dial_code) === normalized(dialCode));
    },
    async selectCountry(index) {
      const item = this.countries[index];
      this.form.countryId = normalized(item?.countryId);
      this.form.provinceId = '';
      this.form.cityId = '';
      this.provinces = [];
      this.cities = [];
      const countryCodeItem = findCountryCodeItemByIso2(item?.iso_code_2);
      if (!this.form.phoneCode && countryCodeItem?.dial_code) {
        this.form.phoneCode = countryCodeItem.dial_code;
      }
      await this.loadProvinces();
    },
    async selectProvince(index) {
      const item = this.provinces[index];
      this.form.provinceId = normalized(item?.provinceId);
      this.form.cityId = '';
      this.cities = [];
      await this.loadCities();
    },
    selectCity(index) {
      const item = this.cities[index];
      this.form.cityId = normalized(item?.cityId);
    },
    hydrateFormFromKyc(kycData = {}) {
      this.kycData = kycData || {};
      this.form.addressLine1 = normalized(kycData?.addressLine1);
      this.form.postalCode = normalized(kycData?.postalCode);
      this.form.phoneNumber = normalized(kycData?.phoneNumber);
      this.form.email = normalized(kycData?.email);
      this.form.phoneCode = normalized(kycData?.phoneCountryCode) ? `+${normalized(kycData.phoneCountryCode)}` : this.form.phoneCode;
    },
    async syncAreaFromKyc() {
      if (!this.countries.length) {
        return;
      }

      const country = matchArea(this.countries, {
        idKey: 'countryId',
        idValue: this.kycData?.countryId,
        codeKey: 'iso_code_2',
        codeValue: this.kycData?.country,
        nameValue: this.kycData?.country
      });

      if (!country) {
        return;
      }

      this.form.countryId = normalized(country.countryId);
      const countryCodeItem = findCountryCodeItemByIso2(country?.iso_code_2 || this.kycData?.country);
      if (!this.form.phoneCode && countryCodeItem?.dial_code) {
        this.form.phoneCode = countryCodeItem.dial_code;
      }
      await this.loadProvinces();

      const province = matchArea(this.provinces, {
        idKey: 'provinceId',
        idValue: this.kycData?.provinceId,
        nameValue: this.kycData?.state
      });

      if (!province) {
        return;
      }

      this.form.provinceId = normalized(province.provinceId);
      await this.loadCities();

      const city = matchArea(this.cities, {
        idKey: 'cityId',
        idValue: this.kycData?.cityId,
        nameValue: this.kycData?.city
      });

      if (city) {
        this.form.cityId = normalized(city.cityId);
      }
    },
    async fetchKyc({ address }) {
      const [preResult, kycResult] = await Promise.all([
        pcCardCreatePre({ address }),
        getUserKyc({ address })
      ]);
      this.hydrateFormFromKyc(kycResult || {});
      this.holderStatus = Number(preResult?.holderStatus || kycResult?.holderStatus || 0);
      this.rejectReason = normalized(kycResult?.reason || preResult?.reason);
      await this.syncAreaFromKyc();
      return {
        ...(kycResult || {}),
        holderStatus: this.holderStatus,
        reason: this.rejectReason
      };
    },
    async init({ address }) {
      this.loading = true;
      try {
        await this.ensureCountries();
        if (address) {
          await this.fetchKyc({ address });
        }
      } finally {
        this.loading = false;
      }
    },
    getPickerActions(type) {
      const source = type === 'phone'
        ? countryCodeItems
        : type === 'country'
          ? this.countries
          : type === 'province'
          ? this.provinces
          : this.cities;

      return source.map((item, index) => ({
        name: type === 'phone'
          ? `${item.dial_code || ''} ${item.name || ''}`
          : item.nameCn || item.nameEn || item.name || '--',
        index
      }));
    },
    validate() {
      const addressLine1 = normalized(this.form.addressLine1);
      const postalCode = normalized(this.form.postalCode);
      const phoneCode = normalized(this.form.phoneCode);
      const phoneNumber = normalized(this.form.phoneNumber);

      if (!phoneCode) {
        return 'card.selectCountryCode';
      }
      if (!phoneNumber) {
        return 'card.enterPhone';
      }
      if (!this.form.countryId) {
        return 'card.selectCountry';
      }
      if (this.provinces.length && !this.form.provinceId) {
        return 'card.selectRegion';
      }
      if (this.cities.length && !this.form.cityId) {
        return 'card.selectCity';
      }
      if (!addressLine1) {
        return 'card.enterAddress';
      }
      if (addressLine1.length < 5) {
        return 'card.completeKycAddress';
      }
      if (!postalCode) {
        return 'card.enterPostalCode';
      }
      return '';
    },
    buildSubmitPayload(address) {
      return {
        address: address || null,
        country: this.selectedCountry?.iso_code_2 || '',
        countryId: this.selectedCountry?.countryId || '',
        state: this.selectedProvince?.nameEn || this.selectedProvince?.nameCn || '',
        provinceId: this.selectedProvince?.provinceId || '',
        city: this.selectedCity?.nameEn || this.selectedCity?.nameCn || '',
        cityId: this.selectedCity?.cityId || '',
        addressLine1: normalized(this.form.addressLine1),
        postalCode: normalized(this.form.postalCode),
        phoneNum: normalized(this.form.phoneNumber),
        phoneCode: normalized(this.form.phoneCode).replace('+', ''),
        email: normalized(this.form.email),
        code: normalized(this.form.code),
      };
    },
    async submit({ address }) {
      this.submitting = true;
      try {
        this.resetDiditRuntime();
        const result = await getDiditToken(this.buildSubmitPayload(address));
        this.setVerificationPayload({
          url: result?.url,
          token: result?.token
        });
        return result;
      } finally {
        this.submitting = false;
      }
    },
    async refreshStatus({ address }) {
      this.checking = true;
      try {
        return await this.fetchKyc({ address });
      } finally {
        this.checking = false;
      }
    }
  }
});
